/**
 * Selo do desfecho de um cenário frente ao alvo.
 *
 * A distinção **nunca depende apenas de cor**: cada faixa carrega rótulo em texto e um
 * símbolo próprio. Quem não distingue verde de vermelho precisa conseguir ler o
 * desfecho, e num painel que decide compra isso não é detalhe.
 */

import { Badge, Tooltip } from "@cloudflare/kumo";
import { motion } from "motion/react";
import type { Verdict } from "../fixtures/index.js";
import {
  VERDICT_BADGE_VARIANT,
  VERDICT_HINT,
  VERDICT_LABEL,
  VERDICT_SYMBOL,
  VERDICT_TONE_CLASS,
} from "../theme.js";

/** Entradas do selo: o desfecho e o porte com que ele deve ser desenhado. */
interface Props {
  readonly verdict: Verdict;
  /** Porte grande para a tela de verificação rápida; pequeno para tabelas. */
  readonly size?: "large" | "small";
}

/**
 * Desenha o desfecho como selo, com cor, símbolo e rótulo em texto.
 *
 * O selo grande "salta" ao trocar de desfecho: é o único elemento da tela que muda de
 * cor quando o cenário muda, e a mola chama o olho para ele.
 */
export function VerdictBadge({ verdict, size = "small" }: Props): React.ReactElement {
  const label = `${VERDICT_SYMBOL[verdict]} ${VERDICT_LABEL[verdict]}`;

  if (size === "small") {
    return (
      <Tooltip content={VERDICT_HINT[verdict]} render={<span className="inline-flex" />}>
        <Badge variant={VERDICT_BADGE_VARIANT[verdict]}>{label}</Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={VERDICT_HINT[verdict]}
      render={
        <motion.div
          key={verdict}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className={`inline-flex items-center gap-3 rounded-xl border-2 px-7 py-4 text-3xl font-bold tracking-wide sm:px-9 sm:py-5 sm:text-4xl ${VERDICT_TONE_CLASS[verdict]}`}
        />
      }
    >
      {label}
    </Tooltip>
  );
}
